if (!customElements.get('compare-equipment')) {
  customElements.define(
    'compare-equipment',
    class CompareEquipment extends HTMLElement {
      constructor() {
        super();
        this.leftSelect = null;
        this.rightSelect = null;
        this.productData = {};
        this.initialized = false;
        this.openSide = null;
        this._boundDocClick = this._onDocumentClick.bind(this);
        this._boundKeydown = this._onDocumentKeydown.bind(this);
      }

      disconnectedCallback() {
        this._dropdownAbort?.abort();
        this._dropdownAbort = null;
        this.openSide = null;
        this.initialized = false;
      }

      getWrap(side) {
        return this.querySelector(`[data-compare-select-wrap="${side}"]`);
      }

      syncSelectUIFromValue(side) {
        const select = this.querySelector(`[data-compare-select="${side}"]`);
        const wrap = this.getWrap(side);
        if (!select || !wrap) return;
        const val = select.value;
        const label = wrap.querySelector('.compare-equipment__select-label');
        const opt = select.selectedOptions[0];
        if (label && opt) label.textContent = opt.textContent;
        wrap.querySelectorAll('.compare-equipment__dropdown-item').forEach((btn) => {
          const active = btn.dataset.value === val;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-selected', active ? 'true' : 'false');
        });
      }

      setDropdownOpen(side, open) {
        const wrap = this.getWrap(side);
        if (!wrap) return;
        const trigger = wrap.querySelector(`[data-compare-trigger="${side}"]`);
        const list = wrap.querySelector(`[data-compare-dropdown="${side}"]`);
        if (!trigger || !list) return;

        if (open) {
          if (this.openSide && this.openSide !== side) {
            this.setDropdownOpen(this.openSide, false);
          }
          list.hidden = false;
          wrap.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
          this.openSide = side;
        } else {
          list.hidden = true;
          wrap.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
          if (this.openSide === side) this.openSide = null;
        }
      }

      initCustomDropdowns() {
        this._dropdownAbort?.abort();
        const ac = new AbortController();
        this._dropdownAbort = ac;
        const o = { signal: ac.signal };

        ['left', 'right'].forEach((side) => {
          const wrap = this.getWrap(side);
          const trigger = wrap?.querySelector(`[data-compare-trigger="${side}"]`);
          const list = wrap?.querySelector(`[data-compare-dropdown="${side}"]`);
          const select = this.querySelector(`[data-compare-select="${side}"]`);
          if (!wrap || !trigger || !list || !select) return;

          trigger.addEventListener(
            'click',
            (e) => {
              e.preventDefault();
              e.stopPropagation();
              const isOpen = wrap.classList.contains('is-open');
              this.setDropdownOpen(side, !isOpen);
            },
            o
          );

          list.addEventListener(
            'click',
            (e) => {
              const btn = e.target.closest('.compare-equipment__dropdown-item');
              if (!btn || !list.contains(btn)) return;
              e.preventDefault();
              e.stopPropagation();
              const blockId = btn.dataset.value;
              if (!blockId) return;
              if (select.value === blockId) {
                this.setDropdownOpen(side, false);
                return;
              }
              select.value = blockId;
              select.dispatchEvent(new Event('change', { bubbles: true }));
              this.setDropdownOpen(side, false);
            },
            o
          );
        });

        document.addEventListener('click', this._boundDocClick, o);
        document.addEventListener('keydown', this._boundKeydown, o);
      }

      _onDocumentClick(e) {
        if (!this.openSide) return;
        const wrap = this.getWrap(this.openSide);
        if (wrap && wrap.contains(e.target)) return;
        this.setDropdownOpen(this.openSide, false);
      }

      _onDocumentKeydown(e) {
        if (e.key !== 'Escape' || !this.openSide) return;
        this.setDropdownOpen(this.openSide, false);
      }

      connectedCallback() {
        // Use requestAnimationFrame to ensure DOM is fully ready
        requestAnimationFrame(() => {
          // Cache DOM elements
          this.leftSelect = this.querySelector('[data-compare-select="left"]');
          this.rightSelect = this.querySelector('[data-compare-select="right"]');

          // Initialize product data from data store
          this.initProductData();

          // Set up event listeners
          if (this.leftSelect) {
            this.leftSelect.addEventListener('change', (e) => {
              this.updateCard('left', e.target.value);
              this.syncSelectUIFromValue('left');
            });
          }

          if (this.rightSelect) {
            this.rightSelect.addEventListener('change', (e) => {
              this.updateCard('right', e.target.value);
              this.syncSelectUIFromValue('right');
            });
          }

          this.initCustomDropdowns();

          // Initial update
          if (this.leftSelect && this.leftSelect.value) {
            this.updateCard('left', this.leftSelect.value);
          }
          if (this.rightSelect && this.rightSelect.value) {
            this.updateCard('right', this.rightSelect.value);
          }
          this.syncSelectUIFromValue('left');
          this.syncSelectUIFromValue('right');

          // Listen for Shopify design mode events
          if (window.Shopify && Shopify.designMode) {
            document.addEventListener('shopify:block:select', (e) => {
              this.handleBlockSelect(e);
            });
          }

          this.initialized = true;
        });
      }

      initProductData() {
        // Get data from hidden data store
        const dataStore = this.querySelector('.compare-equipment__data-store');
        if (!dataStore) {
          console.warn('Compare Equipment: Data store not found');
          return;
        }

        const dataElements = dataStore.querySelectorAll('[data-product-data]');
        if (dataElements.length === 0) {
          console.warn('Compare Equipment: No product data found');
          return;
        }

        dataElements.forEach(el => {
          const blockId = el.dataset.productData;
          const name = el.dataset.productName;
          const subtitle = el.dataset.productSubtitle;
          const level = el.dataset.productLevel;
          const image = el.querySelector('[data-product-image]')?.innerHTML || '';
          
          // Parse specs from structured data
          const specRows = el.querySelectorAll('[data-product-specs] .spec-row');
          let specsHTML = '';
          specRows.forEach(row => {
            const label = row.dataset.specLabel;
            const value = row.dataset.specValue;
            if (label && value) {
              specsHTML += `<tr class="compare-equipment__specs-row">
                <td class="compare-equipment__specs-label">${label}</td>
                <td class="compare-equipment__specs-value">${value}</td>
              </tr>`;
            }
          });

          this.productData[blockId] = {
            name,
            subtitle,
            level,
            image,
            specs: specsHTML
          };
        });

        console.log('Compare Equipment: Product data initialized', Object.keys(this.productData).length, 'products');
      }

      updateCard(position, blockId) {
        const data = this.productData[blockId];
        if (!data) {
          console.warn('Compare Equipment: No data for block', blockId);
          return;
        }

        // Get card elements
        const card = this.querySelector(`[data-compare-card="${position}"]`);
        if (!card) {
          console.warn('Compare Equipment: Card not found for position', position);
          return;
        }

        // Update image
        const imageWrapper = card.querySelector('.compare-equipment__card-image-wrapper');
        if (imageWrapper && data.image) {
          imageWrapper.innerHTML = data.image;
        }

        // Update title (subtitle from data)
        const titleEl = card.querySelector('.compare-equipment__card-title');
        if (titleEl) titleEl.textContent = data.subtitle || '';

        // Update level
        const levelEl = card.querySelector('.compare-equipment__card-level');
        if (levelEl) levelEl.textContent = data.level || '';

        // Update specs table - replace entire table to maintain structure
        const specsContainer = card.querySelector('.compare-equipment__specs');
        if (specsContainer && data.specs) {
          // Create a complete table structure with tbody wrapper
          const completeTableHTML = `<table class="compare-equipment__specs-table"><tbody>${data.specs}</tbody></table>`;
          specsContainer.innerHTML = completeTableHTML;
        }
      }

      handleBlockSelect(event) {
        const blockId = event.detail?.blockId;
        if (!blockId || !this.productData[blockId]) return;

        // Check if this block exists in our options
        const leftOption = this.leftSelect?.querySelector(`option[value="${blockId}"]`);
        const rightOption = this.rightSelect?.querySelector(`option[value="${blockId}"]`);

        if (leftOption) {
          this.leftSelect.value = blockId;
          this.updateCard('left', blockId);
          this.syncSelectUIFromValue('left');
        }
        if (rightOption) {
          this.rightSelect.value = blockId;
          this.updateCard('right', blockId);
          this.syncSelectUIFromValue('right');
        }
      }
    }
  );
}
